import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionHeaders, getSessionIdFromJwt } from "@/lib/session";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import ConnectFormRenderer, { type ConnectFormField, buildFormValues, extractConnectionParams } from "./ConnectFormRenderer";
import {
  Plus,
  Pencil,
  Trash2,
  Database,
  RefreshCw,
  ChevronDown,
  ChevronLeft,
  Loader2,
  Table2,
  CircleCheck,
  CircleX,
  CircleDashed,
  Plug,
} from "lucide-react";
import { cn } from "@/lib/utils";

type ConnectionStatus = "idle" | "checking" | "connected" | "failed";

interface StatusInfo {
  status: ConnectionStatus;
  message?: string;
  latency_ms?: number;
}

const DB_TYPES = [
  { value: "postgresql", label: "PostgreSQL", defaultPort: 5432 },
  { value: "mysql", label: "MySQL", defaultPort: 3306 },
  { value: "clickhouse", label: "ClickHouse", defaultPort: 8123 },
  { value: "bigquery", label: "BigQuery", defaultPort: null },
  { value: "mongodb", label: "MongoDB", defaultPort: 27017 },
] as const;

type DbType = (typeof DB_TYPES)[number]["value"];

interface ColumnInfo {
  column_name: string;
  data_type: string;
  is_nullable: string;
}

interface TableSchema {
  table_name: string;
  columns: ColumnInfo[];
}

interface SelectedSchema {
  [tableName: string]: string[];
}

interface SyncingJob {
  id: string;
  connection_id: string;
  client_id: string;
  profile_id: string | null;
  job_name: string;
  connection_name: string;
  source_table_name: string;
  dest_table_name: string | null;
  columns: unknown;
  offset_column: string | null;
  connect_type: string;
  last_sync_start: string | null;
  last_sync_end: string | null;
  last_sync_status: string;
  no_of_records: number;
  size: number;
  created_at: string;
  updated_at: string;
}

interface ConnectionParams {
  host?: string;
  port?: number;
  database_name?: string;
  username?: string;
  password?: string;
  connection_string?: string;
  ssl_enabled?: boolean;
}

interface DbConnection {
  id: string;
  name: string;
  db_type: DbType;
  connection_params: ConnectionParams;
  enabled: boolean;
  logo: string | null;
  logo_color: string | null;
  created_at: string;
  syncing_jobs?: SyncingJob[];
}

const DatabaseConnections = () => {
  const { profile } = useAuth();
  const [connections, setConnections] = useState<DbConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [selectedDbType, setSelectedDbType] = useState<DbType>("postgresql");
  const [saving, setSaving] = useState(false);

  const [statuses, setStatuses] = useState<Record<string, StatusInfo>>({});

  const [selectedConn, setSelectedConn] = useState<DbConnection | null>(null);
  const [schema, setSchema] = useState<TableSchema[]>([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [selections, setSelections] = useState<SelectedSchema>({});
  const [savingSchema, setSavingSchema] = useState<string | null>(null);
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [offsetColumns, setOffsetColumns] = useState<Record<string, string>>({});
  const [columnDescriptions, setColumnDescriptions] = useState<Record<string, Record<string, string>>>({});
  const [allowRemote, setAllowRemote] = useState(false);
  const [connectTypes, setConnectTypes] = useState<Record<string, string>>({});
  const [connectFormFields, setConnectFormFields] = useState<ConnectFormField[] | null>(null);
  const [dynamicFormValues, setDynamicFormValues] = useState<Record<string, unknown>>({});

  const fetchConnections = async () => {
    if (!profile?.client_id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("pipeline_source_connector")
      .select("id, name, db_type, connection_params, enabled, logo, logo_color, created_at")
      .eq("client_id", profile.client_id)
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    else {
      const conns = (data as unknown as DbConnection[]) || [];
      // Fetch syncing jobs for all connections
      const { data: jobs } = await supabase.from("pipeline_jobs" as any).select("*").eq("client_id", profile.client_id);
      if (jobs) {
        const jobsByConn: Record<string, SyncingJob[]> = {};
        (jobs as unknown as SyncingJob[]).forEach((j) => {
          if (!jobsByConn[j.connection_id]) jobsByConn[j.connection_id] = [];
          jobsByConn[j.connection_id].push(j);
        });
        conns.forEach((c) => {
          c.syncing_jobs = jobsByConn[c.id] || [];
        });
      }
      setConnections(conns);
    }
    setLoading(false);
  };

  const testConnection = async (connId: string) => {
    setStatuses((prev) => ({ ...prev, [connId]: { status: "checking" } }));
    try {
      const { data, error } = await supabase.functions.invoke("db-test-connection", {
        headers: await getSessionHeaders(),
        body: { connection_id: connId },
      });
      if (error) throw error;
      if (data?.ok) {
        setStatuses((prev) => ({
          ...prev,
          [connId]: { status: "connected", message: data.message, latency_ms: data.latency_ms },
        }));
      } else {
        setStatuses((prev) => ({
          ...prev,
          [connId]: { status: "failed", message: data?.message || "Connection failed" },
        }));
      }
    } catch (e: any) {
      setStatuses((prev) => ({
        ...prev,
        [connId]: { status: "failed", message: e.message || "Connection failed" },
      }));
    }
  };

  const testAllConnections = async (conns: DbConnection[]) => {
    conns.forEach((c) => testConnection(c.id));
  };

  useEffect(() => {
    fetchConnections();
  }, [profile?.client_id]);

  useEffect(() => {
    if (connections.length > 0) {
      testAllConnections(connections);
    }
  }, [connections.length]);

  const handleDbTypeChange = async (val: string) => {
    const dbType = val as DbType;
    setSelectedDbType(dbType);
    const lookupType = dbType === "postgresql" ? "postgres" : dbType;
    const fields = await fetchConnectForm(lookupType);
    setConnectFormFields(fields);
    setDynamicFormValues(fields ? buildFormValues(fields) : {});
  };

  const fetchConnectForm = async (dbType: string) => {
    const { data } = await supabase
      .from("all_connectors")
      .select("connect_form")
      .eq("db_type", dbType)
      .limit(1)
      .maybeSingle();
    return (data as any)?.connect_form as ConnectFormField[] | null;
  };

  const openCreate = async () => {
    setEditId(null);
    setSelectedDbType("postgresql");
    const fields = await fetchConnectForm("postgres");
    setConnectFormFields(fields);
    setDynamicFormValues(fields ? buildFormValues(fields) : {});
    setDialogOpen(true);
  };

  const openEdit = async (conn: DbConnection) => {
    const params = conn.connection_params || {};
    setEditId(conn.id);
    setSelectedDbType(conn.db_type);
    const lookupType = conn.db_type === "postgresql" ? "postgres" : conn.db_type;
    const fields = await fetchConnectForm(lookupType);
    setConnectFormFields(fields);
    setDynamicFormValues(fields ? buildFormValues(fields, params as Record<string, unknown>, conn.name) : {});
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const connName = ((dynamicFormValues.name as string) || "").trim();

    if (!connName) {
      toast.error("Connection name is required");
      return;
    }
    if (!connectFormFields) {
      toast.error("Form definition not loaded");
      return;
    }
    setSaving(true);

    const connectionParams = extractConnectionParams(connectFormFields, dynamicFormValues);
    // Preserve existing password if not provided during edit
    if (editId && !dynamicFormValues.password) {
      const existing = connections.find((c) => c.id === editId);
      if (existing?.connection_params?.password) {
        connectionParams.password = existing.connection_params.password;
      }
    }

    const lookupType = selectedDbType === "postgresql" ? "postgres" : selectedDbType;
    const payload: Record<string, unknown> = {
      name: connName,
      db_type: selectedDbType,
      connection_params: connectionParams,
    };

    // Look up logo from all_connectors catalog
    if (!editId) {
      const { data: catalogEntry } = await supabase
        .from("all_connectors")
        .select("logo, color")
        .eq("db_type", lookupType)
        .limit(1)
        .maybeSingle();
      if (catalogEntry) {
        payload.logo = catalogEntry.logo || null;
        payload.logo_color = catalogEntry.color || null;
      }
    }

    let error;
    if (editId) {
      ({ error } = await supabase
        .from("pipeline_source_connector")
        .update(payload as any)
        .eq("id", editId));
    } else {
      payload.client_id = profile?.client_id;
      ({ error } = await supabase.from("pipeline_source_connector").insert(payload as any));
    }

    if (error) toast.error(error.message);
    else {
      toast.success(editId ? "Connection updated" : "Database connection added");
      setDialogOpen(false);
      setEditId(null);
      fetchConnections();
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("pipeline_source_connector").delete().eq("id", id);
    if (error) toast.error(error.message);
    else {
      toast.success("Connection removed");
      setConnections((c) => c.filter((x) => x.id !== id));
      if (selectedConn?.id === id) setSelectedConn(null);
    }
  };

  const toggleEnabled = async (id: string, enabled: boolean) => {
    const { error } = await supabase
      .from("pipeline_source_connector")
      .update({ enabled } as any)
      .eq("id", id);
    if (error) toast.error(error.message);
    else setConnections((c) => c.map((x) => (x.id === id ? { ...x, enabled } : x)));
  };

  // --- Schema fetching & selection ---

  const openSchemaView = async (conn: DbConnection) => {
    setSelectedConn(conn);
    // Build selections, offset columns, and descriptions from syncing_jobs
    const sel: SelectedSchema = {};
    const offsets: Record<string, string> = {};
    const descs: Record<string, Record<string, string>> = {};
    const cTypes: Record<string, string> = {};
    (conn.syncing_jobs || []).forEach((j) => {
      const cols = Array.isArray(j.columns) ? (j.columns as any[]).map((c: any) => c.column_name || c) : [];
      sel[j.source_table_name] = cols;
      if (j.offset_column) offsets[j.source_table_name] = j.offset_column;
      if (j.connect_type) cTypes[j.source_table_name] = j.connect_type;
      // Load saved descriptions
      if (Array.isArray(j.columns)) {
        const tableDescs: Record<string, string> = {};
        (j.columns as any[]).forEach((c: any) => {
          if (c.description) tableDescs[c.column_name || c] = c.description;
        });
        if (Object.keys(tableDescs).length > 0) descs[j.source_table_name] = tableDescs;
      }
    });
    setSelections(sel);
    setOffsetColumns(offsets);
    setColumnDescriptions(descs);
    setConnectTypes(cTypes);
    // Check if this connector type allows remote
    setAllowRemote(false);
    if (conn.db_type) {
      supabase
        .from("all_connectors")
        .select("allow_remote")
        .eq("db_type", conn.db_type)
        .limit(1)
        .then(({ data }) => {
          if (data && data.length > 0) {
            setAllowRemote(!!(data[0] as any).allow_remote);
          }
        });
    }
    // Store existing offsets to merge with schema defaults later
    const existingOffsets = { ...offsets };
    setExpandedTables(new Set());
    setSchemaLoading(true);
    setSchema([]);

    try {
      const { data, error } = await supabase.functions.invoke("db-schema", {
        headers: await getSessionHeaders(),
        body: { connection_id: conn.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const fetchedSchema: TableSchema[] = data.schema || [];
      setSchema(fetchedSchema);
      // Auto-set offset column: prefer updated_at/update_at, then created_at, then id
      const autoOffsets = { ...existingOffsets };
      fetchedSchema.forEach((t) => {
        if (!autoOffsets[t.table_name]) {
          const updatedAtCol = t.columns.find((c) => c.column_name === "updated_at" || c.column_name === "update_at");
          if (updatedAtCol) {
            autoOffsets[t.table_name] = updatedAtCol.column_name;
          } else {
            const createdAtCol = t.columns.find((c) => c.column_name === "created_at");
            if (createdAtCol) {
              autoOffsets[t.table_name] = createdAtCol.column_name;
            } else {
              const idCol = t.columns.find((c) => c.column_name === "id");
              if (idCol) {
                autoOffsets[t.table_name] = idCol.column_name;
              }
            }
          }
        }
      });
      setOffsetColumns(autoOffsets);
    } catch (e: any) {
      toast.error(e.message || "Failed to fetch schema");
    } finally {
      setSchemaLoading(false);
    }
  };

  const isTableSelected = (tableName: string) => !!selections[tableName];
  const isColumnSelected = (tableName: string, colName: string) => selections[tableName]?.includes(colName) ?? false;

  const toggleTable = (tableName: string, columns: ColumnInfo[]) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (next[tableName]) {
        delete next[tableName];
      } else {
        next[tableName] = columns.map((c) => c.column_name);
      }
      return next;
    });
  };

  const toggleColumn = (tableName: string, colName: string, allColumns: ColumnInfo[]) => {
    setSelections((prev) => {
      const next = { ...prev };
      const current = next[tableName] || [];
      if (current.includes(colName)) {
        const updated = current.filter((c) => c !== colName);
        if (updated.length === 0) delete next[tableName];
        else next[tableName] = updated;
      } else {
        next[tableName] = [...current, colName];
      }
      return next;
    });
  };

  const selectAll = () => {
    const all: SelectedSchema = {};
    schema.forEach((t) => {
      all[t.table_name] = t.columns.map((c) => c.column_name);
    });
    setSelections(all);
  };

  const deselectAll = () => {
    setSelections({});
  };

  const allSelected =
    schema.length > 0 &&
    schema.every((t) => !!selections[t.table_name] && selections[t.table_name].length === t.columns.length);

  const toggleExpandTable = (tableName: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev);
      if (next.has(tableName)) next.delete(tableName);
      else next.add(tableName);
      return next;
    });
  };

  const scheduleSyncForTable = async (tableName: string) => {
    if (!selectedConn || !profile?.client_id) return;
    const cols = selections[tableName];
    if (!cols || cols.length === 0) {
      toast.error("Select at least one column first");
      return;
    }
    const offset = offsetColumns[tableName];
    if (!offset) {
      toast.error("Please select an offset column before scheduling sync");
      return;
    }
    setSavingSchema(tableName);
    try {
      // Delete existing job for this table+connection
      await supabase.from("pipeline_jobs" as any).delete().eq("connection_id", selectedConn.id).eq("source_table_name", tableName);

      const tableSchema = schema.find((t) => t.table_name === tableName);
      const tableDescs = columnDescriptions[tableName] || {};
      const columnsJson = cols.map((colName) => {
        const colInfo = tableSchema?.columns.find((c) => c.column_name === colName);
        return {
          column_name: colName,
          data_type: colInfo?.data_type || "text",
          is_nullable: colInfo?.is_nullable || "YES",
          ...(tableDescs[colName] ? { description: tableDescs[colName] } : {}),
        };
      });

      // Retrieve the logged-in user's session ID so the DB trigger can
      // forward it as x-session-id to the external pipeline API.
      const { data: sessionData } = await supabase.auth.getSession();
      const sessionId = getSessionIdFromJwt(sessionData.session?.access_token);

      const connectType = connectTypes[tableName] || "sync";
      const { error } = await supabase.from("pipeline_jobs" as any).insert({
        connection_id: selectedConn.id,
        client_id: profile.client_id,
        name: `${selectedConn.name}_${tableName}`,
        job_name: `${profile.client_id}_${selectedConn.name}_${tableName}`,
        connection_name: selectedConn.name,
        source_table_name: tableName,
        columns: columnsJson,
        offset_column: offset,
        connect_type: connectType,
        logo: selectedConn.logo || null,
        logo_color: selectedConn.logo_color || null,
        ...(sessionId ? { session_id: sessionId } : {}),
      } as any);
      if (error) throw error;

      toast.success(`Sync scheduled for ${tableName}`);
      // Refresh syncing jobs
      const { data: jobs } = await supabase.from("pipeline_jobs" as any).select("*").eq("connection_id", selectedConn.id);
      const updatedJobs = (jobs as unknown as SyncingJob[]) || [];
      setConnections((c) => c.map((x) => (x.id === selectedConn.id ? { ...x, syncing_jobs: updatedJobs } : x)));
      setSelectedConn((prev) => (prev ? { ...prev, syncing_jobs: updatedJobs } : prev));
    } catch (e: any) {
      toast.error(e.message || "Failed to schedule sync");
    }
    setSavingSchema(null);
  };

  const selectedCount = Object.keys(selections).length;
  const totalColumns = Object.values(selections).reduce((sum, cols) => sum + cols.length, 0);

  // --- Schema selection detail view ---
  if (selectedConn) {
    return (
      <div className="flex-1 overflow-auto p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedConn(null)}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="text-xl font-semibold">{selectedConn.name}</h2>
            <p className="text-sm text-muted-foreground">
              {DB_TYPES.find((d) => d.value === selectedConn.db_type)?.label} — Select tables & columns to sync
            </p>
          </div>
        </div>

        {schemaLoading ? (
          <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Connecting and fetching schema…</span>
          </div>
        ) : schema.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
              <Database className="h-10 w-10 text-muted-foreground" />
              <p className="text-muted-foreground">No tables found in this database</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {selectedCount} table{selectedCount !== 1 ? "s" : ""} • {totalColumns} column
                {totalColumns !== 1 ? "s" : ""} selected
              </p>
              <Button variant="outline" size="sm" onClick={allSelected ? deselectAll : selectAll}>
                {allSelected ? "Deselect All" : "Select All"}
              </Button>
            </div>

            <div className="space-y-1">
              {schema.map((table) => {
                const tSelected = isTableSelected(table.table_name);
                const selectedCols = selections[table.table_name] || [];
                const allColsSelected = tSelected && selectedCols.length === table.columns.length;
                const someColsSelected = selectedCols.length > 0 && selectedCols.length < table.columns.length;
                const isExpanded = expandedTables.has(table.table_name);

                return (
                  <Collapsible
                    key={table.table_name}
                    open={isExpanded}
                    onOpenChange={() => toggleExpandTable(table.table_name)}
                  >
                    <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2 hover:bg-accent/50 transition-colors">
                      <Checkbox
                        checked={allColsSelected ? true : someColsSelected ? "indeterminate" : false}
                        onCheckedChange={() => toggleTable(table.table_name, table.columns)}
                      />
                      <CollapsibleTrigger asChild>
                        <button className="flex flex-1 items-center gap-2 text-sm font-medium text-left">
                          <Table2 className="h-3.5 w-3.5 text-muted-foreground" />
                          {table.table_name}
                          <span className="text-xs text-muted-foreground font-normal">
                            ({table.columns.length} col{table.columns.length !== 1 ? "s" : ""})
                          </span>
                          <ChevronDown
                            className={cn(
                              "ml-auto h-3 w-3 transition-transform text-muted-foreground",
                              isExpanded && "rotate-180",
                            )}
                          />
                        </button>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent>
                      <div className="ml-8 border-l border-border pl-3 py-1 space-y-0.5">
                        {table.columns.map((col) => {
                          const isOffset = offsetColumns[table.table_name] === col.column_name;
                          const desc = columnDescriptions[table.table_name]?.[col.column_name] || "";
                          return (
                            <div
                              key={col.column_name}
                              className="rounded px-2 py-1 text-sm hover:bg-accent/30 transition-colors space-y-1"
                            >
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={isColumnSelected(table.table_name, col.column_name)}
                                  onCheckedChange={() => toggleColumn(table.table_name, col.column_name, table.columns)}
                                />
                                <span className="flex-1">{col.column_name}</span>
                                <span className="text-xs text-muted-foreground">{col.data_type}</span>
                                {col.is_nullable === "YES" && (
                                  <span className="text-xs text-muted-foreground italic">nullable</span>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOffsetColumns((prev) => {
                                      const next = { ...prev };
                                      if (next[table.table_name] === col.column_name) delete next[table.table_name];
                                      else next[table.table_name] = col.column_name;
                                      return next;
                                    });
                                  }}
                                  className={cn(
                                    "text-[10px] px-1.5 py-0.5 rounded border transition-colors shrink-0",
                                    isOffset
                                      ? "bg-primary text-primary-foreground border-primary"
                                      : "border-border text-muted-foreground hover:border-primary/50",
                                  )}
                                  title={isOffset ? "Remove as offset column" : "Set as offset column"}
                                >
                                  {isOffset ? "Offset ✓" : "Offset"}
                                </button>
                              </div>
                              <div className="ml-6">
                                <Input
                                  placeholder="Add column description..."
                                  className="h-7 text-xs"
                                  value={desc}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setColumnDescriptions((prev) => {
                                      const next = { ...prev };
                                      if (!next[table.table_name]) next[table.table_name] = {};
                                      next[table.table_name] = { ...next[table.table_name], [col.column_name]: val };
                                      return next;
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Per-table connect type & Schedule Syncing button */}
                      <div className="ml-8 pl-3 pt-2 pb-1 space-y-2">
                        {tSelected && allowRemote && (
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground shrink-0">Connect Type</Label>
                            <Select
                              value={connectTypes[table.table_name] || "sync"}
                              onValueChange={(val) => setConnectTypes((prev) => ({ ...prev, [table.table_name]: val }))}
                            >
                              <SelectTrigger className="h-7 w-[120px] text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sync">Sync</SelectItem>
                                <SelectItem value="remote">Remote</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {tSelected && (
                          <Button
                            size="sm"
                            variant={offsetColumns[table.table_name] ? "default" : "outline"}
                            disabled={savingSchema === table.table_name}
                            onClick={() => scheduleSyncForTable(table.table_name)}
                          >
                            {savingSchema === table.table_name ? "Scheduling…" : "Schedule Syncing"}
                          </Button>
                        )}
                        {tSelected && !offsetColumns[table.table_name] && (
                          <p className="text-xs text-destructive mt-1">Select an offset column first</p>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  // --- Connection list view ---
  return (
    <div className="flex-1 overflow-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">SQL Databases</h2>
          <p className="text-sm text-muted-foreground">Connect external databases for querying</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchConnections} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-1", loading && "animate-spin")} />
            Refresh
          </Button>
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) setEditId(null);
            }}
          >
            <DialogTrigger asChild>
              <Button size="sm" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-1" /> Add Connection
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editId ? "Edit Database Connection" : "Add Database Connection"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Database Type</Label>
                  <Select value={selectedDbType} onValueChange={handleDbTypeChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DB_TYPES.map((d) => (
                        <SelectItem key={d.value} value={d.value}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {connectFormFields ? (
                  <ConnectFormRenderer
                    fields={connectFormFields}
                    values={dynamicFormValues}
                    onChange={(name, value) => setDynamicFormValues((prev) => ({ ...prev, [name]: value }))}
                    isEdit={!!editId}
                  />
                ) : (
                  <div className="flex items-center gap-2 py-4 justify-center text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading form...
                  </div>
                )}

                <Button className="w-full" onClick={handleSave} disabled={saving || !connectFormFields}>
                  {saving ? "Saving…" : editId ? "Save Changes" : "Save Connection"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : connections.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
            <Database className="h-10 w-10 text-muted-foreground" />
            <p className="text-muted-foreground">No database connections yet.</p>
          </CardContent>
        </Card>
      ) : (
        <TooltipProvider>
          <div className="grid gap-3">
            {connections.map((c) => {
              const info = DB_TYPES.find((d) => d.value === c.db_type);
              const params = c.connection_params || {};
              const syncedTables = c.syncing_jobs?.length || 0;
              const si = statuses[c.id] || { status: "idle" as ConnectionStatus };
              return (
                <Card
                  key={c.id}
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => openSchemaView(c)}
                >
                  <CardHeader className="flex flex-row items-center justify-between py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent">
                        <Database className="h-4 w-4 text-accent-foreground" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-sm font-medium">{c.name}</CardTitle>
                          {si.status === "checking" && (
                            <Badge variant="outline" className="gap-1 text-xs font-normal">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Checking
                            </Badge>
                          )}
                          {si.status === "connected" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="default"
                                  className="gap-1 text-xs font-normal bg-green-600 hover:bg-green-600/80"
                                >
                                  <CircleCheck className="h-3 w-3" />
                                  Connected
                                  {si.latency_ms != null && (
                                    <span className="ml-0.5 opacity-75">{si.latency_ms}ms</span>
                                  )}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>{si.message}</TooltipContent>
                            </Tooltip>
                          )}
                          {si.status === "failed" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="destructive" className="gap-1 text-xs font-normal">
                                  <CircleX className="h-3 w-3" />
                                  Failed
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">{si.message || "Connection failed"}</TooltipContent>
                            </Tooltip>
                          )}
                          {si.status === "idle" && (
                            <Badge variant="secondary" className="gap-1 text-xs font-normal">
                              <CircleDashed className="h-3 w-3" />
                              Unknown
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {info?.label} {params.host ? `• ${params.host}` : ""}{" "}
                          {params.database_name ? `• ${params.database_name}` : ""}
                          {syncedTables > 0 && ` • ${syncedTables} table${syncedTables !== 1 ? "s" : ""} synced`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => testConnection(c.id)}
                            disabled={si.status === "checking"}
                          >
                            {si.status === "checking" ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Plug className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Test connection</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(c)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit connection</TooltipContent>
                      </Tooltip>
                      <Switch checked={c.enabled} onCheckedChange={(v) => toggleEnabled(c.id, v)} />
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </TooltipProvider>
      )}
    </div>
  );
};

export default DatabaseConnections;
